import { Check, Copy } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';

export type FullPageErrorLogProps = {
  label: string;
  display: string;
  clipboard: string;
};

export function FullPageErrorLog({
  label,
  display,
  clipboard
}: FullPageErrorLogProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'FullPageErrorLog' });
  const [copied, setCopied] = useState(false);
  const CopyFeedbackIcon = copied ? Check : Copy;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(clipboard);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [clipboard]);

  return (
    <div
      aria-label={label}
      className="mb-5 flex w-full max-w-192 items-center gap-3 rounded-md border border-border-default bg-bg-default p-3 text-left"
    >
      <code
        className="min-w-0 flex-1 truncate font-mono text-base text-fg-muted"
        title={display}
      >
        {display}
      </code>
      <Button
        type="button"
        variant="ghost"
        size="md"
        layout="icon"
        className="shrink-0"
        onClick={() => void handleCopy()}
        aria-label={copied ? t('copied') : t('copy')}
      >
        <CopyFeedbackIcon
          key={copied ? 'copied' : 'copy'}
          aria-hidden="true"
          className="animate-in fade-in zoom-in-95 duration-500 ease-in-out"
        />
      </Button>
    </div>
  );
}
